import { TextFieldModule } from '@angular/cdk/text-field';
import { Component, computed, inject, signal } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { TranslateService } from '@ngx-translate/core';
import { FileItem, FileUploader, FileUploadModule } from 'ng2-file-upload';
import { catchError, EMPTY, forkJoin, tap } from 'rxjs';

import { MyDriveFileModel } from '@models/files/mydrive-file.model';
import { ProjectFileModel } from '@models/files/project-file.model';
import {
  FileChunkParams,
  FileItemModel,
  MimeTypesEnum,
  MimeTypesNotRecognizedByBrowserDict,
  PreviewableImageMimeTypes,
  ThumbnailsDict,
} from '@models/module/fields/main';
import { AddChunkedVersionParams, ProjectFilesAddNewModel, SaveFileInfoParams } from '@module-instance/my-drive/common';
import {
  FileFactoryService,
  FileServiceType,
  IFileService,
} from '@module-instance/my-drive/common/services/file-factory.service';
import { FILE_SIZES } from '@shared/consts';
import { CipoDialogHeaderComponent } from '@shared/dialogs';
import { CipoDialogModule } from '@shared/modules';
import { NotificationService, UserService } from '@shared/services';
import { DictionaryStore } from '@signal-store/dictionary.store';
import { UserStore } from '@signal-store/user.store';

@Component({
  selector: 'app-add-new',
  imports: [
    FileUploadModule,
    CipoDialogModule,
    MatProgressBarModule,
    MatCheckboxModule,
    FormsModule,
    ReactiveFormsModule,
    MatInputModule,
    TextFieldModule,
    CipoDialogHeaderComponent,
  ],
  providers: [FileFactoryService, NotificationService],
  templateUrl: './add-new.component.html',
  styleUrl: './add-new.component.scss',
})
export class AddNewComponent {
  dialogData = inject<ProjectFilesAddNewModel>(MAT_DIALOG_DATA);
  dialogRef = inject(MatDialogRef<AddNewComponent>);
  translate = inject(TranslateService);
  userService = inject(UserService);
  fileFactoryService = inject(FileFactoryService);
  notificate = inject(NotificationService);
  userStore = inject(UserStore);
  private readonly dictionaryStore = inject(DictionaryStore);

  private supportedMimeTypes: Set<string> = null;
  private readonly mimeTypesReady = signal(!this.dialogData.validateMimeTypes);

  name = new FormControl(this.dialogData.name || '', {
    validators: [Validators.required, Validators.maxLength(255)],
  });
  valid = signal(false);
  uploading = signal(false);
  multiple = signal(true);
  createNewVersion = signal(true);
  filesToUpload: FileItemModel[] = [];
  dialogTitle = computed(() => {
    switch (this.dialogData.modalType) {
      case 'newFile':
        return 'fileExplorer.upload';
      case 'newFolder':
        return 'fileExplorer.addNewFolder';
      case 'rename':
        return 'fileExplorer.rename';
    }
  });

  uploader: FileUploader;
  hasBaseDropZoneOver: boolean;

  private fileService: IFileService<ProjectFileModel | MyDriveFileModel>;

  constructor() {
    this.fileService = this.fileFactoryService.getServiceType(
      this.dialogData.entityInstanceId ? FileServiceType.ProjectFiles : FileServiceType.MyDrive,
    );

    if (this.dialogData.fileId) {
      this.multiple.set(false);
    }

    if (this.dialogData.modalType !== 'newFile') {
      this.name.valueChanges.subscribe(() => {
        this.valid.set(this.name.valid);
      });
    }

    this.configureUploader();

    if (this.dialogData.validateMimeTypes) {
      this.loadSupportedMimeTypes();
    }
  }

  private configureUploader() {
    const headers = this.userService.getUserHeaders();

    this.uploader = new FileUploader({
      url: '',
      headers: Object.keys(headers).map(key => ({ name: key, value: headers[key] })),
    });

    this.uploader.onBeforeUploadItem = item => {
      item.withCredentials = false;
    };
    this.uploader.onAfterAddingFile = file => {
      let fileType = file.file.type;
      if (!fileType) {
        // browser doesn't recognize the mime type, try to get it from the file extension
        const extension = file.file.name.split('.').pop();
        fileType = MimeTypesNotRecognizedByBrowserDict[extension];
      }
      if (!fileType) {
        console.error('File type not recognized for file', file.file.name);
      }
      file.file['mimeType'] = fileType;
      if (this.rejectIfUnsupported(file)) {
        return;
      }
      this.setUploadUrl(file);
      const reader = new FileReader();
      reader.onload = event => {
        let preview: string | ArrayBuffer;
        if (PreviewableImageMimeTypes.includes(fileType as MimeTypesEnum)) {
          preview = event.target.result;
        } else {
          preview = ThumbnailsDict[fileType as keyof typeof ThumbnailsDict] || ThumbnailsDict.default;
        }
        file.file['preview'] = preview;
      };
      reader.readAsDataURL(file._file);
      this.updateUploadValidity();
    };
    this.uploader.onCompleteItem = () => {
      this.updateUploadValidity();
    };
  }

  private loadSupportedMimeTypes() {
    this.dictionaryStore.getMimeTypesDict().subscribe({
      next: mimeTypes => {
        this.supportedMimeTypes = new Set(mimeTypes.map(({ description }) => description).filter(Boolean));
        [...this.uploader.queue].forEach(file => this.rejectIfUnsupported(file));
        this.mimeTypesReady.set(true);
        this.updateUploadValidity();
      },
      error: () => {
        this.supportedMimeTypes = new Set();
        this.mimeTypesReady.set(false);
        this.updateUploadValidity();
        this.notificate.error('fileExplorer.mimeTypesLoadFailed');
      },
    });
  }

  private rejectIfUnsupported(file: FileItem): boolean {
    const mimeType = file.file['mimeType'] as string;
    if (!this.supportedMimeTypes || (mimeType && this.supportedMimeTypes.has(mimeType))) {
      return false;
    }
    this.notificate.error('fileExplorer.unsupportedMimeType', {
      name: file.file.name,
      type: mimeType || file.file.name.split('.').pop() || '',
    });
    this.uploader.removeFromQueue(file);
    this.updateUploadValidity();
    return true;
  }

  private updateUploadValidity() {
    this.valid.set(!!this.uploader.queue.length && this.mimeTypesReady());
  }

  removeFile(file: FileItem) {
    file.remove();
    this.updateUploadValidity();
  }

  fileOver(e: boolean): void {
    this.hasBaseDropZoneOver = e;
  }

  toggleCreateVersion(checked: boolean) {
    this.createNewVersion.set(checked);
    this.uploader.queue.forEach(file => this.setUploadUrl(file));
  }

  private setUploadUrl(file: FileItem) {
    const mimeType = file.file['mimeType'];
    file.url = this.dialogData.fileId
      ? this.fileService.getFileNewVersionUrl({
          fileId: this.dialogData.fileId,
          mimeType,
          entityInstanceId: this.dialogData.entityInstanceId,
        })
      : this.fileService.getFileUploadUrl({
          parentId: this.dialogData.parentId,
          mimeType,
          entityInstanceId: this.dialogData.entityInstanceId,
          isCreateVersion: this.dialogData.allowCreateVersion ? this.createNewVersion() : undefined,
        });
  }

  noteFor(item: FileItem): string {
    return (item.file['note'] as string) ?? '';
  }

  setNote(item: FileItem, note: string) {
    item.file['note'] = note;
  }

  private toUploadedFile(response: unknown): FileItemModel {
    if (typeof response === 'string') {
      return this.toUploadedFile(JSON.parse(response));
    }
    // AddVersion / AddChunkedVersion return the new version id, not a file DTO.
    if (typeof response === 'number') {
      return { id: this.dialogData.fileId, versionId: response } as FileItemModel;
    }
    return response as FileItemModel;
  }

  private applyNote(item: FileItem, file: FileItemModel): FileItemModel {
    const note = this.noteFor(item).trim();
    if (note) {
      file.note = note;
    }
    return file;
  }

  private closeWithUploadedFiles() {
    const files = this.filesToUpload;
    const withNotes = this.dialogData.persistNotes ? files.filter(f => f.note && f.versionId) : [];

    if (!withNotes.length) {
      this.dialogRef.close({ files });
      return;
    }

    forkJoin(
      withNotes.map(f =>
        this.fileService.saveFile({ id: f.id, versionId: f.versionId, note: f.note }, this.dialogData.entityInstanceId),
      ),
    ).subscribe({
      next: () => this.dialogRef.close({ files }),
      error: () => this.dialogRef.close({ files }),
    });
  }

  save() {
    if (this.dialogData.modalType === 'newFile') {
      if (!this.valid() || !this.mimeTypesReady()) {
        return;
      }
      this.uploader.onSuccessItem = (item: FileItem, response: string) => {
        this.filesToUpload.push(this.applyNote(item, this.toUploadedFile(response)));
      };
      this.uploader.onErrorItem = (item: FileItem, response: string) => {
        const message = JSON.parse(response);
        this.notificate.error(message?.messages[0]?.message ?? 'errors.unidentifiedError');
      };
      this.uploader.onCompleteAll = () => {
        const errorFile = this.uploader.queue.find(file => file.isError);
        const notUploaded = this.uploader.queue.find(file => !file.isUploaded);
        if (errorFile || notUploaded) {
          this.uploading.set(false);
          return;
        }
        this.closeWithUploadedFiles();
      };
      this.uploader.queue.forEach(file => {
        if (file.isError) {
          file.isError = false;
          file.isUploaded = false;
        }
      });
      this.uploading.set(true);
      this.uploader.queue.forEach(file => {
        if (file.file.size > FILE_SIZES.maxFileSize) {
          this.uploadFileInChunks(file);
        } else {
          file.upload();
        }
      });
    } else {
      this.dialogRef.close({ name: this.name.value });
    }
  }

  private uploadFileInChunks(fileItem: FileItem) {
    const file = fileItem._file;
    const chunks = Math.ceil(file.size / FILE_SIZES.chunkSize);
    let fileId: string;

    this.fileService
      .generateFileId()
      .pipe(
        tap(id => (fileId = id)),
        catchError(() => {
          fileItem.isError = true;
          this.uploading.set(false);
          return EMPTY;
        }),
      )
      .subscribe(() => {
        fileItem.progress = 100 / chunks / 2;
        const uploadChunk = (start: number, end: number) => {
          const uploadChunkParams: FileChunkParams = { start, end, chunks, file };
          const formData = this.fileService.getChunkData(uploadChunkParams);

          this.fileService.uploadChunk(fileId, formData).subscribe({
            next: () => {
              if (end < file.size) {
                fileItem.progress = (100 / chunks) * (end / FILE_SIZES.chunkSize);
                uploadChunk(end, Math.min(end + FILE_SIZES.chunkSize, file.size));
              } else {
                fileItem.progress = 100;
                const subscriber = this.dialogData.fileId
                  ? this.saveFileVersion(fileId, chunks, fileItem)
                  : this.saveFileInfo(fileId, chunks, fileItem);
                subscriber
                  .pipe(
                    catchError(() => {
                      fileItem.isError = true;
                      this.uploading.set(false);
                      return EMPTY;
                    }),
                  )
                  .subscribe(responseFile => {
                    fileItem.isUploaded = true;
                    fileItem.isSuccess = true;
                    this.filesToUpload.push(this.applyNote(fileItem, this.toUploadedFile(responseFile)));
                    if (this.filesToUpload.length === this.uploader.queue.length) {
                      this.closeWithUploadedFiles();
                    }
                  });
              }
            },
            error: () => {
              fileItem.isError = true;
              this.uploading.set(false);
            },
          });
        };

        uploadChunk(0, Math.min(FILE_SIZES.chunkSize, file.size));
      });
  }

  private saveFileInfo(fileId: string, chunks: number, fileItem: FileItem) {
    const file = fileItem._file;
    const body = { fileName: file.name, bytes: file.size };
    const params: SaveFileInfoParams = {
      fileId,
      mimeType: file.type,
      totalBlocks: chunks,
      parentId: this.dialogData.parentId,
      roleId: 0,
      entityInstanceId: this.dialogData.entityInstanceId,
      isCreateVersion: this.createNewVersion(),
    };

    return this.fileService.saveFileInfo(params, body);
  }

  private saveFileVersion(fileId: string, chunks: number, fileItem: FileItem) {
    const file = fileItem._file;
    const body = { fileName: file.name, bytes: file.size };
    const params: AddChunkedVersionParams = {
      fileId: this.dialogData.fileId,
      mimeType: file.type,
      totalBlocks: chunks,
      roleId: 0,
      entityInstanceId: this.dialogData.entityInstanceId,
      physicalId: fileId,
    };

    return this.fileService.addChunkedVersion(params, body);
  }
}
